import { useMemo, useState } from 'react'
import cn from 'classnames'

import DoubleToggleButton from 'components/DoubleToggleButton'
import CheckboxTree from 'components/CheckboxTree'
import CheckboxIndeterminate from 'components/CheckboxIndeterminate'

import { MENUS_DRAWER_DOUBLE_TOGGLE_BUTTON_OPTIONS } from 'resources'

import { getRevenueCentersWithSelectedMenus } from 'utils/menusDrawer/getRevenueCentersWithSelectedMenus'
import { getAllRevenueCentersAndMenusUuids } from 'utils/menusDrawer/getAllRevenueCentersAndMenusUuids'
import { getIsCheckedRevenueCenterCheckbox } from 'utils/menusDrawer/getIsCheckedRevenueCenterCheckbox'
import { getIsIndeterminateState } from 'utils/menusDrawer/getIsIndeterminateState'
import { onChangeCheckbox } from 'utils/menusDrawer/onChangeCheckbox'
import { getSelectedRevenueCentersUuids } from 'utils/menusDrawer/getSelectedRevenueCentersUuids'

const MenusDrawerCheckboxTree = ({
  selectedRevenueCentersAndMenusUuids,
  setSelectedRevenueCentersAndMenusUuids,
  revenueCentersAndMenus,
}) => {
  const allRevenueCentersAndMenusUuids =
    getAllRevenueCentersAndMenusUuids(revenueCentersAndMenus)

  const [isAllMenusChecked, setIsAllMenusChecked] = useState(
    allRevenueCentersAndMenusUuids.length ===
      selectedRevenueCentersAndMenusUuids.length
  )
  const [selectedToggleButton, setSelectedToggleButton] =
    useState('All Menus')

  const handleChangeDoubleToggleButton = (uuid) => {
    setSelectedToggleButton(uuid)

    // if state === 'Selected Menus' we have to add all revenue centers
    // uuids to selectedRevenueCentersAndMenusUuids in order to keep
    // them selected
    if (uuid === 'Selected Menus') {
      // get all revenueCentersWithAssignedMenus
      const revenueCentersWithSelectedMenus =
        getRevenueCentersWithSelectedMenus(
          revenueCentersAndMenus,
          selectedRevenueCentersAndMenusUuids
        )

      // get uuids of revenue centers
      const selectedRevenueCentersUuids =
        revenueCentersWithSelectedMenus?.map(({ uuid }) => uuid)

      // if selectedRevenueCentersAndMenusUuids includes uuid of above
      // selectedRevenueCentersUuids we must filter them out in order to
      // not dupicate state uuids
      const selectedRevenueCentersUuidsUuidsWithoutCurrentSelected =
        selectedRevenueCentersUuids.filter(
          (uuid) =>
            !selectedRevenueCentersAndMenusUuids.includes(uuid)
        )

      setSelectedRevenueCentersAndMenusUuids([
        ...selectedRevenueCentersAndMenusUuids,
        ...selectedRevenueCentersUuidsUuidsWithoutCurrentSelected,
      ])
    } else if (uuid === 'All Menus') {
      // if state === 'All Menus' we have to check again which revenue centers
      // should be in selectedRevenueCentersAndMenusUuids state. This time we check them by
      // all selected children

      // return selectedRevenueCentersAndMenusUuids without all revenueCentersUuids
      const menuUuidsWithoutRevenueCentersUuids =
        selectedRevenueCentersAndMenusUuids.filter(
          (uuid) => !uuid.startsWith('revenue-center-')
        )

      // check again which revenue centers should be selected so should be
      // in selectedRevenueCentersAndMenusUuids state. Revenue center will be selected
      // when all his children will be selected
      const selectedRevenueCentersUuids =
        getSelectedRevenueCentersUuids(
          revenueCentersAndMenus,
          menuUuidsWithoutRevenueCentersUuids
        )

      setSelectedRevenueCentersAndMenusUuids([
        ...menuUuidsWithoutRevenueCentersUuids,
        ...selectedRevenueCentersUuids,
      ])
    }
  }

  const handleChangeIndeterminateCheckbox = ({ checked }) => {
    setIsAllMenusChecked(!isAllMenusChecked)
    checked
      ? setSelectedRevenueCentersAndMenusUuids(
          allRevenueCentersAndMenusUuids
        )
      : setSelectedRevenueCentersAndMenusUuids([])
  }

  // filteredMenusAndRevenueCenters are menus based on DoubleToggleButton
  const filteredMenusAndRevenueCenters = useMemo(() => {
    switch (selectedToggleButton) {
      case 'All Menus':
        return revenueCentersAndMenus
      case 'Selected Menus':
        return getRevenueCentersWithSelectedMenus(
          revenueCentersAndMenus,
          selectedRevenueCentersAndMenusUuids
        )
      default:
        return []
    }
  }, [
    revenueCentersAndMenus,
    selectedRevenueCentersAndMenusUuids,
    selectedToggleButton,
  ])

  // we have to return true only when state selectedRevenueCentersAndMenusUuids.length
  // is greater than 0 but less than allRevenueCentersAndMenusUuids.length, when selectedToggleButton
  // is equal "Selected Menus" we have to return false
  const isAllMenusIndeterminate =
    allRevenueCentersAndMenusUuids.length ===
      selectedRevenueCentersAndMenusUuids.length ||
    selectedToggleButton === 'Selected Menus'
      ? false
      : selectedRevenueCentersAndMenusUuids.length > 0
      ? true
      : false

  const isIndeterminate = (uuid) =>
    getIsIndeterminateState(
      uuid,
      filteredMenusAndRevenueCenters,
      selectedRevenueCentersAndMenusUuids
    )

  const isCheckedRevenueCenterCheckbox = (uuid) =>
    getIsCheckedRevenueCenterCheckbox(
      filteredMenusAndRevenueCenters,
      selectedRevenueCentersAndMenusUuids,
      uuid
    )

  return (
    <div className="menus-drawer-checkbox-tree">
      <div className="menus-drawer-checkbox-tree__row">
        <DoubleToggleButton
          options={MENUS_DRAWER_DOUBLE_TOGGLE_BUTTON_OPTIONS}
          onChange={handleChangeDoubleToggleButton}
          selectedUuid={selectedToggleButton}
        />
      </div>
      <div className="menus-drawer-checkbox-tree__row">
        {selectedToggleButton !== 'Selected Menus' && (
          <CheckboxIndeterminate
            checked={
              isAllMenusChecked ||
              allRevenueCentersAndMenusUuids?.length ===
                selectedRevenueCentersAndMenusUuids?.length
            }
            label="All menus"
            indeterminate={isAllMenusIndeterminate}
            handleChange={handleChangeIndeterminateCheckbox}
            className={cn({
              '-active -bold':
                isAllMenusChecked || isAllMenusIndeterminate,
            })}
          />
        )}
      </div>
      <div className="menus-drawer-checkbox-tree__row">
        {filteredMenusAndRevenueCenters?.map(
          ({ name, uuid, menus }) => (
            <CheckboxTree
              key={uuid}
              parentLabel={name}
              parentUuid={uuid}
              children={menus}
              parentChecked={isCheckedRevenueCenterCheckbox(uuid)}
              indeterminate={isIndeterminate(uuid)}
              childrenChecked={(uuid) =>
                selectedRevenueCentersAndMenusUuids?.includes(uuid) ??
                false
              }
              handleChangeCheckbox={(uuid, parentUuid) =>
                onChangeCheckbox(
                  uuid,
                  parentUuid,
                  filteredMenusAndRevenueCenters,
                  selectedRevenueCentersAndMenusUuids,
                  setSelectedRevenueCentersAndMenusUuids
                )
              }
            />
          )
        )}
      </div>
    </div>
  )
}

export default MenusDrawerCheckboxTree
